<template>
  <div class="wysiwyg">
    <template v-for="(contentItem, index) in parsedContent">
      <template v-if="typeof contentItem === 'string'">
        <div
          :key="'staticContent_' + index"
          v-html="contentItem"
        />
      </template>
      <template v-else>
        <component
          :is="contentItem.component | kebab"
          :key="'blockContent_' + index"
          v-bind="contentItem"
        />
      </template>
    </template>
  </div>
</template>

<script>
import { parse } from "@/utils/htmlParser"
export default {
  name: "WysiwygComponent",
  props: {
    content: {
      type: [Array, String],
      default: () => []
    }
  },
  computed: {
    parsedContent () {
      if (typeof this.content === 'string') { return [this.content] }
      if (typeof this.content[0] == 'undefined') { return '' }
      return this.content.map( contentItem => {
        try {
          // Tryparse content as JSON, it might be a block
          let contentObject = JSON.parse(contentItem)
          return contentObject
        } catch (e) {
          // It wasn't a JSON-block, check for any tables, do your thing and return
          if (contentItem.indexOf('<table') !== -1) {
            return this.traverseTable(contentItem)
          }
          return contentItem
        }
      })
    }
  },
  methods: {
    traverseTable (html) {
      let headings = []
      const root = parse(html)
      const allTables = [...root.querySelectorAll("table")]
      allTables.forEach(table => {
        // table header
        const thead = table.querySelectorAll("thead")
        const tr = thead[0] && thead[0].querySelectorAll("tr")
        const headerTDs = tr && tr[0].querySelectorAll("td")
        // table body
        const tbody = table.querySelectorAll("tbody")
        const tbodyTr = tbody[0] && tbody[0].querySelectorAll("tr")

        if (thead && headerTDs) {
          headerTDs.forEach(thead => {
            headings.push(thead.structuredText)
          })
        } else {
          table.rawAttrs += "class=table-no-heading"
          root.innerHTML
        }

        tbodyTr.forEach(tbodyTD => {
          tbodyTD.querySelectorAll("td").forEach((td, key) => {
            if (headings.length > 0) {
              td.rawAttrs = `data-th="${headings[key]}"`
            }
          })
        })
        headings = []
      })
      return root.innerHTML
    }
  }
}
</script>
<style lang="scss">
/* stylelint-disable no-descending-specificity */
.wysiwyg--contentarea {
  padding: 0 0.5rem;
  margin-bottom: 2.5rem;

  p,
  h2,
  h3,
  h4,
  h5,
  ul {
    padding: 0 1.5rem;

    @include media-breakpoint-up(md) {
      padding: 0 10.33333%;
    }

    @include media-breakpoint-up(lg) {
      padding: 0 12.378959%;
    }
  }
}

.wysiwyg {
  word-break: break-word;

  @include media-breakpoint-up(md) {
    padding: 0;
  }

  img {
    border-radius: 0.25rem;
  }

  img + img {
    margin-right: 0;
  }

  div {
    max-width: 100%;
    margin-bottom: 1.5rem;

    &:last-of-type {
      margin-bottom: 0;
    }
  }

  p {
    @include font(text, 3, "normal");

    word-wrap: break-word;
    max-width: 100%;
    margin: 0 0 2rem;

    @include media-breakpoint-up(md) {
      margin-bottom: 2.5rem;
    }

    + p {
      margin-top: 1.5rem;
    }

    &:last-child {
      margin-bottom: 0;
    }
  }

  h2 {
    @include font(rubrik, 3, "normal");

    margin: 0 0 1rem 0;
  }

  h3 {
    @include font(rubrik, 2, "bold");

    margin: 0 0 1rem 0;
  }

  blockquote {
    color: $color-red;
    margin: 0 0 1.5rem 0;

    h3,
    p {
      padding: 0;
    }

    p {
      @include font(text, 5, "normal");
    }
  }

  ol {
    margin: 0 1.75rem;

    li {
      padding-bottom: 1rem;
      padding-left: 1rem;

      @include font(text,3, "normal");
    }
  }

  ul {
    li {
      position: relative;
      padding-left: 2.5rem;
      padding-bottom: 1rem;

      @include font(text,3, "normal");

      &::before {
        background-image: url("data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyNCIgaGVpZ2h0PSIyNCIgdmlld0JveD0iMCAwIDMyIDMyIiBmaWxsPSIjZTMwMDBiIj4KICA8cGF0aCBmaWxsLXJ1bGU9ImV2ZW5vZGQiIGQ9Ik0yNS45MDk2MTcsOS4wMjg1MTE3OCBMMjQuNzM5MzkwMSw4LjA1OTUxMTc4IEMyNC42MzIxNzAzLDcuOTY5NTExNzggMjQuNDcxODUxMyw3Ljk4MzUxMTc4IDI0LjM4MDk2OTgsOC4wODg1MTE3OCBMMTMuNzQ2ODEyOSwyMC40NTI1MTE4IEw3LjYxOTk3MDA4LDEzLjA4ODUxMTggQzcuNTI5MDg4NTgsMTIuOTgyNTExOCA3LjM2Nzc0ODM4LDEyLjk2OTUxMTggNy4yNjA1Mjg2NCwxMy4wNTg1MTE4IEw2LjA5MDMwMTY2LDE0LjAyNzUxMTggQzUuOTgyMDYwNzcsMTQuMTE2NTExOCA1Ljk2OTgwNzA4LDE0LjI3NDUxMTggNi4wNjA2ODg1OCwxNC4zODA1MTE4IEwxMi45NjU2NDA0LDIyLjY0NDUxMTggQzEzLjM3NDA5NjYsMjMuMTE3NTExOCAxNC4xMTk1MjkyLDIzLjExNzUxMTggMTQuNTI3OTg1NCwyMi42NDQ1MTE4IEwyNS45MzkyMzAxLDkuMzgwNTExNzggQzI2LjAzMTEzMjgsOS4yNzU1MTE3OCAyNi4wMTY4MzY4LDkuMTE3NTExNzggMjUuOTA5NjE3LDkuMDI4NTExNzgiLz4KPC9zdmc+Cg==");
        background-repeat: no-repeat;
        content: '';
        width: 1.5rem;
        height: 1.5rem;
        position: absolute;
        top: 0;
        left: 0;
        border-radius: 50%;
        background-position: center;
        margin: 0 0.5rem 0 0;
      }

      h2,
      h3 {
        padding: 0;
      }
    }
  }

  ol,
  ul {
    > li:last-child {
      padding-bottom: 0;
    }
  }

  table {
    border: 0.0625rem solid $color-grey-special;
    border-radius: 0.25rem;
    width: 100%;
    overflow: hidden;
    border-spacing: 0;
    margin: 0;
    margin-bottom: 1.5rem;

    @include font(text, 3, "normal");

    td {
      display: flex;
      padding: 1rem;
      word-break: break-word;
      width: auto !important;
      flex-direction: column;
      text-align: left;
      vertical-align: top;
      border-bottom: 0.0625rem solid $color-grey-special;

      &::before {
        content: attr(data-th);
        font-weight: bold;
        min-width: 40%;
        display: block;
        position: relative;
        overflow-wrap: break-word;
        word-break: keep-all;
        max-width: 100%;
        margin-bottom: 0.5rem;

        @include media-breakpoint-up(md) {
          display: none;
        }
      }

      @include media-breakpoint-up(md) {
        word-break: keep-all;
        display: table-cell;
        border-right: 0.0625rem solid $color-grey-special;

        &:last-child {
          border-right: 0;
        }
      }
    }

    th h3 {
      margin: 0;

      @include font(text, 3, "bold");
    }

    td,
    th,
    thead td {
      p,
      h2,
      h3 {
        padding: 0;
      }
    }

    thead {
      display: none;

      @include media-breakpoint-up(md) {
        display: table-header-group;
      }

      td {
        font-weight: bold;

        p,
        h2,
        h3 {
          margin-bottom: 0;
          font-weight: bold;
        }
      }
    }

    th {
      text-align: left;
      border: none;
      vertical-align: top;

      @include media-breakpoint-up(md) {
        display: table-cell;
      }
    }

    tbody > tr:nth-child(odd) {
      background-color: $color-grey-lightest;
    }

    @include media-breakpoint-up(md) {
      tbody > tr:last-child {
        td {
          border-bottom: 0;
        }
      }
    }
  }

  .wysiwig--accordion-block {
    p,
    h2,
    h3 {
      padding: 0;
    }

    h4,
    h5 {
      padding: 0;
    }
  }

  .table-no-heading {
    td {
      &::before {
        display: none;
      }

      p,
      h2,
      h3 {
        padding: 0;
      }
    }
  }

  table:not(.mce-item-table) tbody > tr:last-child {
    td:last-child {
      border-bottom: 0;
    }
  }

  .epi-picture {
    max-width: 100%;
  }

  // width and height can change based on how we are receiving the image from tinnyMce
  .full-width-image {
    width: 100%;
    margin-bottom: 2rem;
  }

  // change the width and margin accordingly when we have the image from TinyMCE
  .block-image {
    margin-bottom: 2rem;
    width: 100%;

    @include media-breakpoint-up(md) {
      width: 49%;
    }

    &.align-left {
      float: left;
      clear: both;
      margin-bottom: 2%;
      margin-right: 2%;
    }

    &.align-right {
      // check if we have float right image variant
      float: right;
    }
  }
}

.layout__info-content .wysiwyg--contentarea {
  padding: 0 0;

  p,
  h2,
  h3,
  h4,
  h5,
  ol,
  ul {
    padding: 0 2rem;

    @include media-breakpoint-up(md) {
      padding: 0 7.625rem;
    }

    @include media-breakpoint-up(lg) {
      padding: 0 15.4375rem;
    }

    @include media-breakpoint-up(xl) {
      padding: 0 17.5rem;
    }
  }

  li {
    strong,
    h2,
    h3,
    h4,
    h5,
    p {
      padding: 0;
      margin: 0;
    }
  }

  blockquote {
    margin: 0 0.5rem 1.5rem;

    @include media-breakpoint-up(md) {
      margin-left: 3.875rem;
      margin-right: 3.875rem;
    }

    @include media-breakpoint-up(lg) {
      margin-left: 10.3125rem;
      margin-right: 10.3125rem;
    }

    @include media-breakpoint-up(xl) {
      margin-left: 6.5rem;
      margin-right: 6.5rem;
    }
  }

  ol {
    margin: 0 1.75rem 2rem;
  }
}
/* stylelint-enable no-descending-specificity */
</style>
